// One fax, either direction, as shown on the client's Faxes tab and the Fax
// Inbox. Outbound requests show their checklist with what has come back.
import { format } from 'date-fns';
import OpenLink from '../OpenLink';
import { useState } from 'react';
import { DOC_TYPE_LABEL, STATUS_LABEL, ITEM_LABEL, resetRequest, unlinkReply, deleteFax } from '../../lib/faxes';
import { useAuth } from '../../hooks/useAuth';

// Same order as the letter: 1. PPOT, 2. LMN, 3. Medical Records.
const ITEM_ORDER = ['ppot', 'lmn', 'records'];

const STATUS_STYLE = {
  matched:   { bg: 'var(--success-bg)', fg: 'var(--success)' },
  assigned:  { bg: 'var(--success-bg)', fg: 'var(--success)' },
  success:   { bg: 'var(--success-bg)', fg: 'var(--success)' },
  pending:   { bg: 'var(--warn-bg)',    fg: 'var(--warn)' },
  unmatched: { bg: 'var(--warn-bg)',    fg: 'var(--warn)' },
  error:     { bg: 'var(--danger-bg)',  fg: 'var(--danger)' },
  failure:   { bg: 'var(--danger-bg)',  fg: 'var(--danger)' },
};

export const StatusChip = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg: 'var(--bg2)', fg: 'var(--text2)' };
  return <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: s.bg, color: s.fg, whiteSpace: 'nowrap' }}>{STATUS_LABEL[status] || status}</span>;
};

const fmtWhen = (iso) => iso ? format(new Date(iso), 'MMM d, yyyy · h:mm a') : '—';

export default function FaxRow({ fax, showClient, onAssign, onChanged, last }) {
  const inbound = fax.direction === 'inbound';
  const items = fax.request_items || {};
  const done = fax.fulfilled_items || {};
  const client = fax.clients;
  const { profile } = useAuth();
  const canDelete = !!onChanged && ['admin', 'manager'].includes(profile?.role);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(''); // two-click confirm; the embedded browser swallows window.confirm()
  const [err, setErr] = useState('');
  const anyDone = Object.keys(done).length > 0;

  const undo = async (key, fn) => {
    if (armed !== key) { setArmed(key); setTimeout(() => setArmed(a => (a === key ? '' : a)), 6000); return; }
    setArmed(''); setBusy(true); setErr('');
    try { await fn(fax.id); onChanged?.(); }
    catch (e) { setErr(e.message || 'Could not update.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: last ? 'none' : '1px solid var(--border)', alignItems: 'flex-start' }}>
      <div title={inbound ? 'Received' : 'Sent'} style={{ width: 34, height: 34, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: inbound ? 'var(--accent-bg)' : 'var(--bg2)', border: '1px solid var(--border)', fontSize: 15 }}>
        {inbound ? '📥' : '📤'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            {inbound ? (DOC_TYPE_LABEL[fax.doc_type] || 'Fax') : (fax.request_title || 'Fax')}
          </span>
          <StatusChip status={fax.status} />
          {showClient && client && (
            <span style={{ fontSize: 12, color: 'var(--accent)' }}>{client.last_name}, {client.first_name} <span style={{ fontFamily: 'monospace', color: 'var(--text3)' }}>{client.client_id}</span></span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
          {inbound
            ? <>{fmtWhen(fax.received_at)}{fax.sender_name ? ` · from ${fax.sender_name}` : ''}{fax.caller_id && fax.caller_id !== 'Unavailable' ? ` · ${fax.caller_id}` : ''}{fax.pages ? ` · ${fax.pages} pg` : ''}</>
            : <>{fmtWhen(fax.sent_at)} · to {fax.recipient_name}{fax.sender_name ? ` (${fax.sender_name})` : ''}{fax.pages ? ` · ${fax.pages} pg` : ''}{fax.sent_by_name ? ` · by ${fax.sent_by_name}` : ''}</>}
        </div>
        {fax.summary && <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 4 }}>{fax.summary}</div>}
        {fax.status === 'failure' && fax.faxage_status && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>Faxage: {fax.faxage_status}</div>}
        {fax.status === 'error' && fax.error && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{fax.error} — retried automatically each hour.</div>}
        {!inbound && Object.keys(items).length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {ITEM_ORDER.filter(k => items[k]).concat(Object.keys(items).filter(k => items[k] && !ITEM_ORDER.includes(k))).map(k => {
              const ok = !!done[k];
              return (
                <span key={k} title={ok ? `Received ${done[k]?.at ? format(new Date(done[k].at), 'MMM d') : ''}` : 'Still outstanding'}
                  style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, border: '1px solid', borderColor: ok ? 'var(--success)' : 'var(--border2)', color: ok ? 'var(--success)' : 'var(--text3)', background: ok ? 'var(--success-bg)' : 'transparent' }}>
                  {ok ? '✓' : '○'} {ITEM_LABEL[k] || k}
                </span>
              );
            })}
          </div>
        )}
        {inbound && fax.reply_to_id && (
          <div style={{ fontSize: 11.5, color: 'var(--success)', marginTop: 4 }}>
            ✓ Answers an open provider request
            {onChanged && <button className="btn btn-ghost btn-sm" disabled={busy} style={{ fontSize: 11, marginLeft: 8, padding: '1px 6px', color: armed === 'unlink' ? 'var(--danger)' : undefined }} onClick={() => undo('unlink', unlinkReply)}>{armed === 'unlink' ? 'Click again to detach' : 'Not a reply'}</button>}
          </div>
        )}
        {!inbound && anyDone && onChanged && (
          <button className="btn btn-ghost btn-sm" disabled={busy} style={{ fontSize: 11, marginTop: 6, padding: '1px 6px', color: armed === 'reset' ? 'var(--danger)' : undefined }} onClick={() => undo('reset', resetRequest)}>{armed === 'reset' ? 'Click again to clear every tick' : 'Reset checklist'}</button>
        )}
        {err && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{err}</div>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
        {fax.drive_url && <OpenLink href={fax.drive_url}>Open</OpenLink>}
        {inbound && onAssign && fax.drive_file_id && (
          <button className="btn btn-ghost btn-sm" onClick={() => onAssign(fax)} style={{ fontSize: 12 }}>
            {fax.client_id ? 'Re-file' : 'File to client'}
          </button>
        )}
        {canDelete && (
          <button className="btn btn-ghost btn-sm" disabled={busy} title="Remove this record and move its Drive copy to the bin" style={{ fontSize: 11, color: armed === 'delete' ? 'var(--danger)' : 'var(--text3)' }} onClick={() => undo('delete', deleteFax)}>
            {armed === 'delete' ? 'Click again to delete' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
